import React, { createContext, useContext, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { boardFetch } from "./api";

export const CompanyContext = createContext(null);
export const useCompany = () => useContext(CompanyContext);

export const RAG = {
  green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  red: "bg-red-500/15 text-red-300 border-red-500/30",
  high: "bg-red-500/15 text-red-300 border-red-500/30",
  medium: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  low: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  neutral: "bg-white/10 text-white/60 border-white/15",
};
const STATUS_RAG = { open: "red", offen: "red", in_progress: "amber", "in Bearbeitung": "amber", beobachtet: "amber", closed: "green", erledigt: "green" };
export const ragOf = (key, val) => {
  if (RAG[val]) return RAG[val];
  if (STATUS_RAG[val]) return RAG[STATUS_RAG[val]];
  return RAG.neutral;
};

export const Badge = ({ value, tone }) => (
  <span className={`inline-block rounded-full border px-2.5 py-0.5 text-[11px] uppercase tracking-wider ${tone || ragOf(null, value)}`}>{value}</span>
);

export const SectionHeader = ({ overline, title, action }) => (
  <div className="mb-8 flex items-end justify-between border-b border-white/10 pb-5">
    <div>
      {overline && <span className="text-[11px] uppercase tracking-[0.3em] text-champagne">{overline}</span>}
      <h1 className="mt-1 font-serif text-3xl font-light text-white md:text-4xl">{title}</h1>
    </div>
    {action}
  </div>
);

export const inlineMd = (s) =>
  s.split(/\*\*(.+?)\*\*/g).map((p, i) => (i % 2 === 1 ? <strong key={i} className="text-white">{p}</strong> : p));

export const MarkdownLite = ({ text }) => (
  <div className="space-y-1.5 text-[15px] leading-relaxed">
    {text.split("\n").map((l, i) => {
      const t = l.trim();
      if (t === "" || t === "---") return null;
      if (l.startsWith("### ")) return <h4 key={i} className="mt-4 font-medium text-white">{l.slice(4)}</h4>;
      if (l.startsWith("## ")) return <h3 key={i} className="mt-5 font-serif text-xl text-white">{l.slice(3)}</h3>;
      if (l.startsWith("# ")) return <h2 key={i} className="font-serif text-2xl text-champagne">{l.slice(2)}</h2>;
      if (l.startsWith("- ") || l.startsWith("* ")) return <li key={i} className="ml-5 list-disc text-white/70">{inlineMd(l.slice(2))}</li>;
      if (l.startsWith("> ")) return <p key={i} className="border-l-2 border-champagne bg-white/[0.03] px-3 py-2 text-white/85">{inlineMd(l.slice(2))}</p>;
      return <p key={i} className="text-white/70">{inlineMd(l)}</p>;
    })}
  </div>
);

export const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" onClick={onClose}>
    <div className="mt-16 w-full max-w-xl border border-white/15 bg-[#121212] p-8" onClick={(e) => e.stopPropagation()} data-testid="board-modal">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="font-serif text-2xl font-light text-white">{title}</h3>
        <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="modal-close"><X size={20} /></button>
      </div>
      {children}
    </div>
  </div>
);

const Field = ({ def, value, onChange }) => {
  const base = "w-full border border-white/15 bg-black/30 px-3 py-2.5 text-sm text-white outline-none focus:border-champagne";
  if (def.type === "textarea" || def.type === "lines")
    return <textarea data-testid={`field-${def.key}`} rows={3} className={base} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  if (def.type === "select")
    return (
      <select data-testid={`field-${def.key}`} className={base} value={value || ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {def.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  return <input data-testid={`field-${def.key}`} type={def.type === "date" ? "date" : "text"} className={base} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
};

const FormModal = ({ title, fields, initial, onSave, onClose }) => {
  const [form, setForm] = useState(() => {
    const f = { ...initial };
    fields.forEach((d) => { if (d.type === "lines" && Array.isArray(f[d.key])) f[d.key] = f[d.key].join("\n"); });
    return f;
  });
  const submit = async () => {
    const payload = { ...form };
    fields.forEach((d) => { if (d.type === "lines" && typeof payload[d.key] === "string") payload[d.key] = payload[d.key].split("\n").map((x) => x.trim()).filter(Boolean); });
    try { await onSave(payload); } catch (e) { toast.error(e.message); }
  };
  return (
    <Modal title={title} onClose={onClose}>
      <div className="space-y-4">
        {fields.map((d) => (
          <div key={d.key}>
            <label className="mb-1.5 block text-xs uppercase tracking-wider text-white/50">{d.label}</label>
            <Field def={d} value={form[d.key]} onChange={(v) => setForm((s) => ({ ...s, [d.key]: v }))} />
          </div>
        ))}
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onClose} className="border border-white/20 px-5 py-2.5 text-xs uppercase tracking-widest text-white/70 hover:text-white">Abbrechen</button>
          <button onClick={submit} data-testid="form-save" className="bg-champagne px-5 py-2.5 text-xs uppercase tracking-widest text-obsidian hover:bg-champagne-light">Speichern</button>
        </div>
      </div>
    </Modal>
  );
};

export const DataSection = ({ overline, title, entity, fields }) => {
  const { company } = useCompany();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const cols = fields.filter((f) => f.table !== false);

  const load = () => boardFetch(`/companies/${company.id}/${entity}`).then(setItems).catch((e) => toast.error(e.message));
  useEffect(() => { if (company) load(); /* eslint-disable-next-line */ }, [company?.id]);

  const save = async (data) => {
    if (data.id) await boardFetch(`/companies/${company.id}/${entity}/${data.id}`, { method: "PUT", json: data });
    else await boardFetch(`/companies/${company.id}/${entity}`, { method: "POST", json: data });
    toast.success("Gespeichert");
    setEditing(null);
    load();
  };
  const remove = async (id) => {
    await boardFetch(`/companies/${company.id}/${entity}/${id}`, { method: "DELETE" });
    load();
  };

  const cell = (f, item) => {
    const v = item[f.key];
    if (v == null || v === "") return <span className="text-white/25">—</span>;
    if (f.type === "lines" && Array.isArray(v)) return <span className="text-white/60">{v.length} Punkte</span>;
    if (f.badge) return <Badge value={v} />;
    return <span className="text-white/75">{String(v)}</span>;
  };

  return (
    <div>
      <SectionHeader overline={overline} title={title} action={
        <button onClick={() => setEditing({})} data-testid={`add-${entity}`} className="flex items-center gap-2 bg-champagne px-4 py-2.5 text-xs uppercase tracking-widest text-obsidian transition-colors hover:bg-champagne-light">
          <Plus size={15} /> Neu
        </button>
      } />
      <div className="overflow-x-auto border border-white/10" data-testid={`table-${entity}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 bg-white/[0.03] text-left text-[11px] uppercase tracking-wider text-white/45">
              {cols.map((f) => <th key={f.key} className="px-4 py-3 font-normal">{f.label}</th>)}
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={cols.length + 1} className="px-4 py-10 text-center text-white/35">Noch keine Einträge</td></tr>
            )}
            {items.map((item) => (
              <tr key={item.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                {cols.map((f) => <td key={f.key} className="px-4 py-3 align-top">{cell(f, item)}</td>)}
                <td className="whitespace-nowrap px-4 py-3 text-right">
                  <button onClick={() => setEditing(item)} className="mr-3 text-white/40 hover:text-champagne" data-testid={`edit-${entity}`}><Pencil size={15} /></button>
                  <button onClick={() => remove(item.id)} className="text-white/40 hover:text-red-400" data-testid={`delete-${entity}`}><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <FormModal title={editing.id ? "Eintrag bearbeiten" : "Neuer Eintrag"} fields={fields} initial={editing} onSave={save} onClose={() => setEditing(null)} />
      )}
    </div>
  );
};
